import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, CheckCircle, AlertCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../services/api";

// ─── Types ────────────────────────────────────────────────────────────────────

type RefundStatus = "refund_initiated" | "processing" | "completed" | "failed";

interface RefundEntry {
  refundId: string;
  amount: number;
  reason: string;
  reasonNote?: string;
  status: RefundStatus;
  externalRefundId?: string;
  initiatedAt: string;
  completedAt?: string;
}

interface PaymentLeg {
  legId: string;
  method: string;
  amount: number;
  amountRefunded: number;
  netAmount: number;
  status: string;
  reference?: string;
  refunds: RefundEntry[];
}

interface PendingRefundRow {
  _id: string;
  transactionId: string;
  orderId: string;
  orderNumber: string | null;
  customerPhone: string | null;
  orderAmount: number;
  totalPaid: number;
  totalRefunded: number;
  netAmount: number;
  currency: string;
  status: string;
  legs: PaymentLeg[];
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const METHOD_LABELS: Record<string, string> = {
  stripe: "Stripe",
  cash: "Cash",
  wallet: "Wallet",
  bonus_wallet: "Bonus Wallet",
  voucher: "Voucher",
  gift_card: "Gift Card",
  loyalty_points: "Loyalty Points",
};

const METHOD_COLORS: Record<string, string> = {
  stripe: "bg-purple-100 text-purple-700",
  cash: "bg-green-100 text-green-700",
  wallet: "bg-blue-100 text-blue-700",
  bonus_wallet: "bg-orange-100 text-orange-700",
  voucher: "bg-pink-100 text-pink-700",
  gift_card: "bg-teal-100 text-teal-700",
  loyalty_points: "bg-yellow-100 text-yellow-700",
};

function fmt(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

const Refunds: React.FC = () => {
  const [rows, setRows] = useState<PendingRefundRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});
  const [completing, setCompleting] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const restaurantId = process.env.REACT_APP_RESTAURANT_ID;

  const fetchPendingRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: PendingRefundRow[] }>(
        "/transactions/pending-refunds",
        { params: { restaurantId } },
      );
      if (res.data.success) setRows(res.data.data);
    } catch {
      toast.error("Failed to load pending refunds");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchPendingRefunds();
  }, [fetchPendingRefunds]);

  // Process all refund_initiated refunds for a transaction (calls Stripe API for stripe legs)
  const handleProcessAll = async (row: PendingRefundRow) => {
    setProcessing((p) => ({ ...p, [row.transactionId]: true }));
    try {
      await api.post(`/transactions/order/${row.orderId}/process-refunds`);
      toast.success(`Refunds processed for Order #${row.orderNumber ?? row.orderId}`);
      fetchPendingRefunds();
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Failed to process refunds");
    } finally {
      setProcessing((p) => ({ ...p, [row.transactionId]: false }));
    }
  };

  // Manually complete a single refund entry (for non-Stripe or manual override)
  const handleCompleteOne = async (
    row: PendingRefundRow,
    legId: string,
    refundId: string,
  ) => {
    const key = `${row.transactionId}-${refundId}`;
    setCompleting((c) => ({ ...c, [key]: true }));
    try {
      await api.patch(
        `/transactions/order/${row.orderId}/legs/${legId}/refunds/${refundId}/complete`,
      );
      toast.success("Refund marked as completed");
      fetchPendingRefunds();
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Failed to complete refund");
    } finally {
      setCompleting((c) => ({ ...c, [key]: false }));
    }
  };

  const toggleExpand = (id: string) =>
    setExpanded((e) => ({ ...e, [id]: !e[id] }));

  // For each row, collect legs that have refund_initiated refunds
  const getInitiatedRefunds = (row: PendingRefundRow) =>
    row.legs.flatMap((leg) =>
      leg.refunds
        .filter((r) => r.status === "refund_initiated")
        .map((r) => ({ leg, refund: r })),
    );

  return (
    <div className="max-w-6xl mx-auto px-3 py-4 sm:px-5 sm:py-6 lg:px-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refunds</h1>
          <p className="text-sm text-gray-500 mt-1">
            Orders with pending refunds awaiting your action
          </p>
        </div>
        <button
          onClick={fetchPendingRefunds}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50 disabled:opacity-50 sm:w-auto"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <RefreshCw size={28} className="animate-spin text-blue-500" />
        </div>
      )}

      {/* Empty */}
      {!loading && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <CheckCircle size={48} className="mb-3 text-green-400" />
          <p className="text-lg font-medium">All caught up</p>
          <p className="text-sm">No pending refunds right now.</p>
        </div>
      )}

      {/* Rows */}
      {!loading &&
        rows.map((row) => {
          const initiatedItems = getInitiatedRefunds(row);
          const totalInitiated = initiatedItems.reduce((s, i) => s + i.refund.amount, 0);
          const isOpen = expanded[row.transactionId];
          const isProcessing = processing[row.transactionId];

          return (
            <div
              key={row.transactionId}
              className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 overflow-hidden"
            >
              {/* Card header */}
              <div className="flex flex-col gap-3 px-4 py-4 sm:px-5 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-gray-900 text-base">
                      Order #{row.orderNumber ?? "—"}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                      Refund Initiated
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    {row.customerPhone && <span>{row.customerPhone}</span>}
                    <span>{fmtDate(row.updatedAt)}</span>
                  </div>
                </div>

                {/* Amounts */}
                <div className="grid w-full grid-cols-3 gap-3 text-sm sm:gap-6 lg:w-auto lg:self-start">
                  <div className="text-center sm:text-right">
                    <p className="min-h-[2rem] text-xs leading-tight text-gray-400 m-0">
                      Order Total
                    </p>
                    <p className="font-semibold text-gray-800">{fmt(row.orderAmount, row.currency)}</p>
                  </div>
                  <div className="text-center sm:text-right">
                    <p className="min-h-[2rem] text-xs leading-tight text-gray-400 m-0">
                      To Refund
                    </p>
                    <p className="font-bold text-red-600">{fmt(totalInitiated, row.currency)}</p>
                  </div>
                  <div className="text-center sm:text-right">
                    <p className="min-h-[2rem] text-xs leading-tight text-gray-400 m-0">
                      Already Refunded
                    </p>
                    <p className="font-semibold text-gray-800">{fmt(row.totalRefunded, row.currency)}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex w-full items-center gap-2 lg:w-auto lg:flex-shrink-0 lg:self-start">
                  <button
                    onClick={() => handleProcessAll(row)}
                    disabled={isProcessing}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60 lg:flex-none"
                  >
                    {isProcessing ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <CheckCircle size={14} />
                    )}
                    {isProcessing ? "Processing…" : "Process All"}
                  </button>
                  <button
                    onClick={() => toggleExpand(row.transactionId)}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    title="Show breakdown"
                  >
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>

              {/* Expandable breakdown */}
              {isOpen && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Refund Breakdown
                  </p>
                  <div className="space-y-2">
                    {initiatedItems.map(({ leg, refund }) => {
                      const key = `${row.transactionId}-${refund.refundId}`;
                      const isCompleting = completing[key];
                      return (
                        <div
                          key={refund.refundId}
                          className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 md:flex-row md:items-center md:justify-between"
                        >
                          <div className="flex items-start gap-3 sm:items-center">
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${METHOD_COLORS[leg.method] ?? "bg-gray-100 text-gray-600"}`}
                            >
                              {METHOD_LABELS[leg.method] ?? leg.method}
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-gray-800 m-0">
                                {fmt(refund.amount, row.currency)}
                              </p>
                              <p className="text-xs text-gray-400">
                                {refund.reason.replace(/_/g, " ")}
                                {refund.reasonNote ? ` — ${refund.reasonNote}` : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                            <div className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs text-amber-600">
                              <Clock size={11} />
                              Initiated {fmtDate(refund.initiatedAt)}
                            </div>
                            {/* Stripe with reference → auto-process via Stripe API */}
                            {leg.method === "stripe" && leg.reference && (
                              <span className="text-xs text-gray-400 italic">
                                Use "Process All" ↑
                              </span>
                            )}
                            {/* Stripe without reference (edge case) */}
                            {leg.method === "stripe" && !leg.reference && (
                              <span className="flex items-center gap-1 text-xs text-red-500">
                                <AlertCircle size={12} />
                                No payment ref
                              </span>
                            )}
                            {/* Cash / wallet / bonus_wallet → manual confirmation */}
                            {leg.method !== "stripe" && (
                              <button
                                onClick={() =>
                                  handleCompleteOne(row, leg.legId, refund.refundId)
                                }
                                disabled={isCompleting}
                                className="flex w-full items-center justify-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-60 sm:w-auto"
                              >
                                {isCompleting ? (
                                  <RefreshCw size={11} className="animate-spin" />
                                ) : (
                                  <CheckCircle size={11} />
                                )}
                                {leg.method === "cash" ? "Confirm Cash Returned" : "Mark Done"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Full leg summary */}
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-5 mb-2">
                    All Payment Legs
                  </p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {row.legs.map((leg) => (
                      <div
                        key={leg.legId}
                        className="flex gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 flex-row items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${METHOD_COLORS[leg.method] ?? "bg-gray-100 text-gray-600"}`}
                          >
                            {METHOD_LABELS[leg.method] ?? leg.method}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            {fmt(leg.amount, row.currency)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 text-right">
                          <span>
                            Refunded:{" "}
                            <strong className="text-red-500">
                              {fmt(leg.amountRefunded, row.currency)}
                            </strong>
                          </span>
                          <br />
                          <span>
                            Net:{" "}
                            <strong className="text-gray-800">
                              {fmt(leg.netAmount, row.currency)}
                            </strong>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default Refunds;
